import { useState } from "react"
import { Section, CodeBlock, InfoBox, FileTree, FileExplanationPanel } from "@/components"
import { reactShadcnProjectStructure } from "@/data/project-structures"
import { reactShadcnFileExplanations, type FileExplanation } from "@/data/file-explanations"

export function ReactShadcnArchitecturePage() {
  const [selectedFile, setSelectedFile] = useState<FileExplanation | null>(null)

  const handleFileClick = (filePath: string) => {
    const fileInfo = reactShadcnFileExplanations[filePath]
    if (fileInfo) {
      setSelectedFile(fileInfo)
    }
  }

  return (
    <>
      <div className="space-y-10">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Architecture</h1>
          <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
            Understand the clean architecture pattern used in this template. The codebase follows 
            a modular structure with clear separation of concerns for maintainability and scalability.
            Click on any file below to see detailed explanations.
          </p>
        </div>

        <InfoBox type="info" title="Interactive File Explorer">
          Click any file in the tree below to view its purpose, key features, and related files.
        </InfoBox>

        <Section title="Project Structure" description="Interactive file tree">
          <FileTree data={reactShadcnProjectStructure} onFileClick={handleFileClick} />
        </Section>

        <Section title="Architectural Pattern" description="Layout → Page → View">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              The template uses a three-layer architecture that separates concerns and makes the codebase easy to navigate:
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Layout</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Provides the overall page structure like sidebars, headers, and footers. 
                  Layouts are reusable across multiple pages.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/layouts/</code>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">Page</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Thin layer that connects routes to views. Handles layout selection and 
                  renders the appropriate view component.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/pages/</code>
              </div>
              
              <div className="bg-card border rounded-lg p-6 space-y-3">
                <h3 className="font-semibold text-lg">View</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Contains the actual UI and business logic. Manages state, data fetching, 
                  and user interactions for each page.
                </p>
                <code className="text-xs bg-muted px-2 py-1 rounded block">src/views/</code>
              </div>
            </div>

            <InfoBox type="info" title="Example Flow">
              Route <code className="bg-muted px-2 py-1 rounded">/dashboard</code> → 
              Page component selects <strong>AppLayout</strong> → 
              Renders <strong>DashboardView</strong> inside layout
            </InfoBox>
          </div>
        </Section>

        <Section title="Key Modules" description="Main architectural components">
          <div className="space-y-6">
            <ModuleCard
              title="Components"
              description="Organized into UI (Shadcn), common (shared), layout, dashboard, and chat components"
              features={[
                "Shadcn UI components (Button, Card, Input, etc.)",
                "Dashboard components (MetricCard, DataTable, ChartCard)",
                "Chat components (ChatList, ChatMessage, ChatInput)",
                "Common components (ErrorBoundary, EmojiPicker, Modal)",
                "Layout components (Sidebar, Header)",
              ]}
            />

            <ModuleCard
              title="Pages & Views"
              description="Follows the Layout → Page → View pattern for clean separation"
              features={[
                "Pages handle routing and layout selection",
                "Views contain business logic and state",
                "Layouts provide reusable page structure",
                "Pages: Dashboard, Chat, Users, Analytics, Settings, Auth",
              ]}
            />

            <ModuleCard
              title="Services"
              description="API communication layer with Axios"
              features={[
                "Centralized Axios client with interceptors",
                "Auth service for authentication",
                "User service for user management",
                "Product service (example CRUD operations)",
                "Request/response interceptors for tokens",
              ]}
            />

            <ModuleCard
              title="State Management"
              description="Zustand stores for global state"
              features={[
                "AuthStore for user and token management",
                "ThemeStore for light/dark mode",
                "SidebarStore for sidebar state",
                "ConfirmationStore for global dialogs",
                "LoaderStore for loading states",
              ]}
            />

            <ModuleCard
              title="Hooks & Utils"
              description="Custom hooks and utility functions"
              features={[
                "useAuth for authentication state",
                "useTheme for theme management",
                "useLocalStorage for persistence",
                "useProducts for data fetching",
                "Format and validation utilities",
              ]}
            />
          </div>
        </Section>

        <Section title="Code Example" description="Layout → Page → View pattern">
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">1. Layout (AppLayout.tsx)</h3>
              <CodeBlock
                code={`import { Outlet } from "react-router-dom"
import { Sidebar } from "@/components/layout/Sidebar"
import { Header } from "@/components/layout/Header"

export function AppLayout() {
  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet /> {/* Child routes render here */}
        </main>
      </div>
    </div>
  )
}`}
                language="typescript"
                filename="src/layouts/AppLayout.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">2. Page (DashboardPage.tsx)</h3>
              <CodeBlock
                code={`import { DashboardView } from "@/views/DashboardView"

export function DashboardPage() {
  // Minimal logic - just renders the view
  return <DashboardView />
}

export default DashboardPage`}
                language="typescript"
                filename="src/pages/DashboardPage.tsx"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">3. View (DashboardView.tsx)</h3>
              <CodeBlock
                code={`import { useEffect, useState } from "react"
import { MetricCard } from "@/components/dashboard/MetricCard"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { dashboardService } from "@/services/dashboard.service"

export function DashboardView() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    dashboardService.getStats()
      .then(setStats)
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard title="Total Users" value={stats.users} trend="up" />
        <MetricCard title="Revenue" value={\`$\${stats.revenue}\`} trend="up" />
        <MetricCard title="Active Sessions" value={stats.sessions} />
      </div>
    </div>
  )
}`}
                language="typescript"
                filename="src/views/DashboardView.tsx"
              />
            </div>
          </div>
        </Section>

        <Section title="Service Layer" description="API communication">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              Services handle all API communication using Axios with built-in interceptors for 
              token management and error handling.
            </p>
            
            <CodeBlock
              code={`import { apiClient } from "./client"
import type { User, LoginDto, RegisterDto } from "@/types/auth.types"

export const authService = {
  // Login user
  async login(credentials: LoginDto) {
    const response = await apiClient.post<{ user: User; token: string }>(
      "/auth/login",
      credentials
    )
    return response.data
  },

  // Register new user
  async register(data: RegisterDto) {
    const response = await apiClient.post<{ user: User; token: string }>(
      "/auth/register",
      data
    )
    return response.data
  },

  // Get current user
  async getCurrentUser() {
    const response = await apiClient.get<User>("/auth/me")
    return response.data
  },

  // Logout
  async logout() {
    await apiClient.post("/auth/logout")
  }
}`}
              language="typescript"
              filename="src/services/api/auth.service.ts"
            />
          </div>
        </Section>

        <Section title="State Management" description="Zustand stores">
          <div className="space-y-6">
            <p className="text-muted-foreground leading-relaxed">
              The template uses Zustand for lightweight state management. Each store is focused 
              on a specific domain (auth, theme, UI state).
            </p>
            
            <CodeBlock
              code={`import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { User } from "@/types/auth.types"

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  setAuth: (user: User, token: string) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      
      setAuth: (user, token) =>
        set({ user, token, isAuthenticated: true }),
      
      clearAuth: () =>
        set({ user: null, token: null, isAuthenticated: false })
    }),
    {
      name: "auth-storage" // localStorage key
    }
  )
)`}
              language="typescript"
              filename="src/store/authStore.ts"
            />
            
            <div className="mt-6">
              <h4 className="font-semibold mb-4">Using the store in components:</h4>
              <CodeBlock
                code={`import { useAuthStore } from "@/store/authStore"

function UserProfile() {
  const { user, clearAuth } = useAuthStore()

  const handleLogout = () => {
    clearAuth()
    // Additional logout logic...
  }

  return (
    <div>
      <p>Welcome, {user?.name}</p>
      <button onClick={handleLogout}>Logout</button>
    </div>
  )
}`}
                language="typescript"
              />
            </div>
          </div>
        </Section>

        <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
          <h2 className="text-2xl font-bold">Best Practices</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Keep views focused on a single responsibility</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Move reusable logic to custom hooks</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use the service layer for all API calls</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Define TypeScript types for all data structures</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Keep components small and composable</span>
            </li>
            <li className="flex gap-3">
              <span className="text-primary shrink-0">→</span>
              <span>Use Zustand for global state, local state for component-specific data</span>
            </li>
          </ul>
        </div>
      </div>

      <FileExplanationPanel 
        file={selectedFile} 
        onClose={() => setSelectedFile(null)} 
      />
    </>
  )
}

function ModuleCard({
  title,
  description,
  features
}: {
  title: string
  description: string
  features: string[]
}) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div>
        <h3 className="font-semibold text-lg mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold mb-3">Key Features:</h4>
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex gap-2 text-sm text-muted-foreground">
              <span className="text-primary">→</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
